import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { isPublicAppPath, isProtectedAppPath } from '../utils/publicAccessPaths';
import { accessLogService } from '../services/accessLog.service';
import { trackGaPageView } from '../utils/analytics';
import { summarizeUserAgent } from '../utils/parseUserAgent';

const TITLE_WAIT_MS = 200;
const MIN_ENGAGEMENT_MS = 1000;
const MAX_ENGAGEMENT_MS = 4 * 60 * 60 * 1000;

function hasAuthToken(): boolean {
  try {
    return Boolean(localStorage.getItem('token'));
  } catch {
    return false;
  }
}

function isTrackablePath(pathname: string): boolean {
  return isPublicAppPath(pathname) || isProtectedAppPath(pathname);
}

function getClientEnvironment(): string {
  return summarizeUserAgent(
    typeof navigator !== 'undefined' ? navigator.userAgent : undefined
  );
}

/**
 * 全 SPA ルートの GA4 page_view、DB page_view / page_engagement（滞在時間）。
 */
export function useSpaPageAnalytics(): void {
  const location = useLocation();
  const { user } = useAuth();
  const lastGaKeyRef = useRef<string>('');
  const lastPageViewRouteRef = useRef<string>('');
  const lastGaSentRef = useRef<{ routeKey: string; title: string }>({ routeKey: '', title: '' });
  const titleObserverRef = useRef<MutationObserver | null>(null);
  const enterTimeRef = useRef<number>(Date.now());
  const activePathRef = useRef<string>('/');
  const prevRouteKeyRef = useRef<string>('');
  const engagementSentForPathRef = useRef<string>('');

  const recordEngagement = (path: string, durationMs: number, beacon = false) => {
    if (!isTrackablePath(path)) return;
    if (durationMs < MIN_ENGAGEMENT_MS || durationMs > MAX_ENGAGEMENT_MS) return;
    const token = `${path}|${enterTimeRef.current}`;
    if (engagementSentForPathRef.current === token) return;
    engagementSentForPathRef.current = token;
    void accessLogService.recordPageEngagement(path, durationMs, {
      clientEnvironment: getClientEnvironment(),
      beacon,
    });
  };

  useEffect(() => {
    const pathname = location.pathname || '/';
    const routeKey = `${pathname}${location.search}`;
    const now = Date.now();
    const loggedIn = Boolean(user) || hasAuthToken();

    if (prevRouteKeyRef.current && prevRouteKeyRef.current !== routeKey) {
      const prevPathname = prevRouteKeyRef.current.split('?')[0] || '/';
      recordEngagement(prevPathname, now - enterTimeRef.current);
      enterTimeRef.current = now;
      engagementSentForPathRef.current = '';
    }
    prevRouteKeyRef.current = routeKey;
    activePathRef.current = pathname;

    const sendGa = () => {
      const title = document.title || '';
      if (
        lastGaSentRef.current.routeKey === routeKey &&
        lastGaSentRef.current.title === title
      ) {
        return;
      }
      lastGaSentRef.current = { routeKey, title };
      trackGaPageView(pathname, { loggedIn });
    };

    if (routeKey !== lastGaKeyRef.current) {
      lastGaKeyRef.current = routeKey;
      lastGaSentRef.current = { routeKey: '', title: '' };
      window.setTimeout(sendGa, TITLE_WAIT_MS);
    }

    if (titleObserverRef.current) {
      titleObserverRef.current.disconnect();
    }
    const titleEl = document.querySelector('title');
    if (titleEl) {
      let titleDebounce: ReturnType<typeof window.setTimeout> | undefined;
      const observer = new MutationObserver(() => {
        if (titleDebounce) window.clearTimeout(titleDebounce);
        titleDebounce = window.setTimeout(sendGa, 80);
      });
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
      titleObserverRef.current = observer;
    }

    if (routeKey !== lastPageViewRouteRef.current && isTrackablePath(pathname)) {
      lastPageViewRouteRef.current = routeKey;
      void accessLogService.recordPublicPageView(pathname, {
        clientEnvironment: getClientEnvironment(),
      });
    }

    return () => {
      if (titleObserverRef.current) {
        titleObserverRef.current.disconnect();
        titleObserverRef.current = null;
      }
    };
  }, [location.pathname, location.search, user]);

  useEffect(() => {
    const onHide = () => {
      const path = activePathRef.current;
      const ms = Date.now() - enterTimeRef.current;
      recordEngagement(path, ms, true);
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') onHide();
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
}
